/**
 * Owner: Masa Bayu
 * File: shape-system.ts
 * Date:Time: 2026-09-13 21:26
 * Description: 22D QXShapeSystem — semantic radius/shape (not CSS border-radius).
 */
import { QX_DESIGN_SCALE_UNIT } from './spacing-system';
export const QX_SHAPE_STEPS = ['none', 'small', 'medium', 'large', 'full'];
export const QX_SHAPE_SYSTEM_MODEL_VERSION = '1.0.0';
export const QX_SHAPE_SYSTEM_FORBIDDEN_KEYS = [
    'react',
    'component',
    'css',
    'className',
    'style',
    'dom',
    'borderRadius',
    'px',
    'rem',
    'em',
    'boxShadow',
    'opacity',
    'blur',
    'renderer',
    'layout',
    'qxw',
    'qxp',
];
function assertNoForbidden(record, label) {
    for (const key of QX_SHAPE_SYSTEM_FORBIDDEN_KEYS) {
        if (key in record) {
            throw new Error(`${label} must not include forbidden field "${key}"`);
        }
    }
}
export function isQXShapeStep(value) {
    return typeof value === 'string' && QX_SHAPE_STEPS.includes(value);
}
export function createQXShapeToken(step, value) {
    if (!isQXShapeStep(step)) {
        throw new Error(`Invalid shape step: ${String(step)}`);
    }
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
        throw new Error('Shape value must be a finite number >= 0');
    }
    return Object.freeze({
        step,
        value: value === 0 ? 0 : value,
        unit: QX_DESIGN_SCALE_UNIT,
    });
}
export function createQXShapeSystem(options) {
    assertNoForbidden({ ...options }, 'Shape system');
    const list = options.shapes.map((token) => {
        assertNoForbidden({ ...token }, 'Shape token');
        return createQXShapeToken(token.step, token.value);
    });
    const seen = new Set();
    for (const item of list) {
        if (seen.has(item.step)) {
            throw new Error(`Duplicate shape step: ${item.step}`);
        }
        seen.add(item.step);
    }
    return Object.freeze({
        modelVersion: QX_SHAPE_SYSTEM_MODEL_VERSION,
        shapes: Object.freeze(list),
    });
}
export function isQXShapeSystem(value) {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        return false;
    }
    const record = value;
    return record.modelVersion === QX_SHAPE_SYSTEM_MODEL_VERSION && Array.isArray(record.shapes);
}
export function validateQXShapeSystem(value) {
    if (!isQXShapeSystem(value)) {
        throw new Error('Invalid shape system');
    }
    createQXShapeSystem(value);
}
export function resolveQXShapeStep(system, step) {
    if (!isQXShapeStep(step)) {
        throw new Error(`Invalid shape step: ${String(step)}`);
    }
    const token = system.shapes.find((item) => item.step === step);
    if (!token) {
        throw new Error(`Shape system does not define step "${step}"`);
    }
    return token.value;
}
/** Immutable upsert of a shape step — returns a new frozen system. */
export function withQXShapeStep(system, step, value) {
    const next = system.shapes.filter((item) => item.step !== step);
    return createQXShapeSystem({
        shapes: [...next, createQXShapeToken(step, value)],
    });
}
//# sourceMappingURL=shape-system.js.map